import React from "react";
import { Star, ShieldCheck, BadgeCheck, Scale, Building2, Quote, Sparkles } from "lucide-react";

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  firm: string;
  city: string;
  rating: number;
  initials: string;
  tag: string;
  tagColor: string;
  gradient: string;
}

const testimonials: Testimonial[] = [
  {
    quote:
      "Tener las actuaciones de la Rama Judicial notificadas al instante y las citas exactas de las Altas Cortes en cada concepto nos ahorra más de 15 horas a la semana. La certeza de que no hay alucinaciones normativas nos da total tranquilidad.",
    author: "Dra. Claudia Mejía",
    role: "Socia Directora",
    firm: "Mejía & Abogados Asociados",
    city: "Medellín",
    rating: 5,
    initials: "CM",
    tag: "Litigio & Altas Cortes",
    tagColor: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20",
    gradient: "from-blue-600 to-indigo-600",
  },
  {
    quote:
      "La recuperación de expensas comunes con botón de pago PSE por WhatsApp aumentó el recaudo prejurídico en más de un 40%. Además, generar el Título Ejecutivo del Art. 48 de la Ley 675 en un solo clic cambió nuestra operación.",
    author: "Dr. Andrés Restrepo",
    role: "Asesor Jurídico & Administrador",
    firm: "Gestión Inmobiliaria & PH",
    city: "Cali",
    rating: 5,
    initials: "AR",
    tag: "Cartera PH Ley 675",
    tagColor: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
    gradient: "from-emerald-600 to-teal-600",
  },
  {
    quote:
      "Las calculadoras laborales con Art. 64 CST y la indexación con IPC del DANE entregan una memoria probatoria perfecta para anexar a demandas y liquidaciones. Es el copiloto jurídico más riguroso y confiable de Colombia.",
    author: "Dr. Fernando Salcedo",
    role: "Litigante & Docente de Derecho",
    firm: "Salcedo Jurídico Laboral",
    city: "Bogotá D.C.",
    rating: 5,
    initials: "FS",
    tag: "Calculadoras & Redacción",
    tagColor: "bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20",
    gradient: "from-purple-600 to-indigo-600",
  },
];

const trustStats = [
  {
    icon: Scale,
    title: "+1.8M de Archivos",
    desc: "Corte Constitucional, CSJ y Consejo de Estado",
  },
  {
    icon: ShieldCheck,
    title: "100% Citas Verificables",
    desc: "Cero alucinaciones con trazabilidad de radicados",
  },
  {
    icon: BadgeCheck,
    title: "Válido ante Juzgados",
    desc: "Memorias de cálculo y títulos ejecutivos certificados",
  },
];

export const Testimonials: React.FC = () => {
  return (
    <section id="testimonials" className="py-20 lg:py-24 bg-slate-50 dark:bg-slate-950 relative overflow-hidden">
      {/* Background ambient lighting */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-gradient-to-tr from-blue-500/10 via-indigo-500/5 to-transparent rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100/80 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-900 text-blue-700 dark:text-blue-300 text-xs font-bold uppercase tracking-wider mb-4 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            <span>Casos de Éxito en Colombia</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-tight mb-4">
            La confianza de abogados y firmas que{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 dark:from-blue-400 dark:to-indigo-300">
              lideran con IA
            </span>
          </h2>

          <p className="text-slate-600 dark:text-slate-300 text-base sm:text-lg leading-relaxed">
            Descubre cómo despachos jurídicos y administraciones de propiedad horizontal optimizan su tiempo, aceleran sus procesos y blindan sus operaciones legales.
          </p>
        </div>

        {/* Testimonials Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto mb-14 items-stretch">
          {testimonials.map((item, index) => (
            <div
              key={index}
              className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-3xl p-7 sm:p-8 shadow-xl shadow-slate-900/5 hover:border-blue-300 dark:hover:border-slate-700 hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between relative group"
            >
              <div>
                {/* Top Row: Stars & Tag */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex gap-1 text-amber-400">
                    {[...Array(item.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${item.tagColor}`}>
                    {item.tag}
                  </span>
                </div>

                {/* Quote Text */}
                <p className="text-sm sm:text-base text-slate-700 dark:text-slate-200 leading-relaxed font-medium mb-6">
                  "{item.quote}"
                </p>
              </div>

              {/* Author Info */}
              <div className="pt-5 border-t border-slate-100 dark:border-slate-800 flex items-center gap-3.5">
                <div
                  className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white font-black text-sm shadow-md shrink-0`}
                >
                  {item.initials}
                </div>
                <div className="overflow-hidden">
                  <h4 className="font-extrabold text-sm text-slate-900 dark:text-white truncate">
                    {item.author}
                  </h4>
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-semibold truncate">
                    {item.role}
                  </p>
                  <p className="text-[11px] text-slate-400 truncate">
                    {item.firm} • {item.city}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Badges Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {trustStats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div
                key={idx}
                className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-4 flex items-center gap-3.5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h5 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white leading-tight">
                    {stat.title}
                  </h5>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-tight">
                    {stat.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default Testimonials;
